/**
 * Where a stored file is shown from. Every place that shows a file (a node, the tiles of the
 * nodes that take it as input, the pickers) asks here, so a file is looked up once and all
 * of them share one address, and a workspace opened again in the same session is answered
 * from memory, in its first render.
 *
 * Local first, in this order: bytes this session still holds; what was resolved before; the
 * workspace's reference map; the storage path this device remembers for the file; the
 * registry (the user's own, then the workspace owner's for shared workspaces). The last three
 * each lead to a look in the local folder. Only then the cloud, so a file that is in the
 * folder is never downloaded.
 */

import {
    extractFileId, getFileMeta, isFileReference, knownStoragePath, rememberStoragePath, resolveFileUrl, sessionBlobForRef, sessionEpoch,
} from './fileRegistry';
import {
    forgetLocalUrl, getLocalFolderAccess, isCurrentLocalUrl, localFolderRevision, localPathForRef, locateLocalFile,
    parseStoragePath, setLocalUrlReferences, storageWorkspaceOf,
} from './localSave';
import { isLegacyMediaRef, LEGACY_MEDIA_MESSAGE } from './mediaStorage';

export interface DisplayMedia {
    /** The address to show the file from, or null when there is none (see error). */
    url: string | null;
    /** The address is of a copy on this device: this session's bytes, or a file of the local folder. */
    isLocal: boolean;
    error: string | null;
    /** Why there is no address: the cloud needs an account, the file is in no registry, the lookup failed, or the reference is from the retired backend. */
    reason?: 'signed-out' | 'missing' | 'failed' | 'legacy';
}

export interface DisplayOptions {
    userId?: string;
    /** The open workspace: its folder is where a local copy is looked for first. Without it only the cloud is asked. */
    workspaceName?: string;
    /** Owner of a shared workspace; their registry is asked when the file is not in the user's own. */
    ownerUserId?: string;
}

/** A cloud address is used this long before the registry is asked for it again (it would also show a file that was replaced). */
const CLOUD_URL_TTL_MS = 30 * 60_000;
/** A file that was not in the folder is looked for there again after this long, in case it was put there outside the app. */
const LOCAL_RECHECK_MS = 5 * 60_000;

interface Kept {
    url: string;
    isLocal: boolean;
    /** When the address was got. */
    madeAt: number;
    /**
     * For a cloud address: when the local folder was last searched for the file in vain, by
     * the workspace it was searched from, and the folder's state then (localFolderRevision).
     * Seen from another workspace, after the folder changed, or after a while, the folder is
     * searched again before the cloud address is used.
     */
    revision: number;
    searched: Map<string, number>;
}

/** What was resolved in this session, by account and reference. Only answers are kept, never failures. */
const resolved = new Map<string, Kept>();
/**
 * Lookups in progress, so every place asking for a file at the same moment shares one. The
 * folder's state is part of the key: a lookup begun before the folder became readable (or
 * changed) does not answer one begun after.
 */
const inFlight = new Map<string, Promise<DisplayMedia>>();
const keptKey = (ref: string, userId?: string): string => `${userId || ''}|${ref}`;
/** The reference each local address was resolved from, for code that must save the reference and never the address. */
const referencesByLocalUrl = new Map<string, string>();
let referencesShared = false;

/** What was resolved belongs to the session it was resolved in: after a sign-out it is dropped before anything is answered. */
let epoch = -1;
const inThisSession = (): void => {
    const now = sessionEpoch();
    if (now === epoch) return;
    epoch = now;
    resolved.clear();
    inFlight.clear();
    referencesByLocalUrl.clear();
};

const answer = (kept: Kept): DisplayMedia => ({ url: kept.url, isLocal: kept.isLocal, error: null });

/** Whether a kept answer can be shown as it is. */
const stillGood = (kept: Kept, workspaceName: string | undefined): boolean => {
    if (kept.isLocal) return isCurrentLocalUrl(kept.url);
    const now = Date.now();
    const searchedAt = kept.revision === localFolderRevision() ? kept.searched.get(workspaceName || '') : undefined;
    return searchedAt !== undefined && now - searchedAt < LOCAL_RECHECK_MS && now - kept.madeAt < CLOUD_URL_TTL_MS;
};

/**
 * What is already known of a reference, without any lookup: nothing to show, an address that
 * is not a file reference (used as it is), a retired reference, this session's bytes, or an
 * earlier answer that still holds. Undefined when the reference has to be resolved.
 */
export const peekDisplayUrl = (ref: string | null | undefined, options: DisplayOptions = {}): DisplayMedia | undefined => {
    if (!ref) return { url: null, isLocal: false, error: null };
    if (isLegacyMediaRef(ref)) return { url: null, isLocal: false, error: LEGACY_MEDIA_MESSAGE, reason: 'legacy' };
    if (!isFileReference(ref)) return { url: ref, isLocal: false, error: null };
    inThisSession();
    // Bytes this session still holds (a video generated a moment ago): nothing to read or download
    const session = sessionBlobForRef(ref);
    if (session) return { url: session, isLocal: true, error: null };
    const kept = resolved.get(keptKey(ref, options.userId));
    return kept && stillGood(kept, options.workspaceName) ? answer(kept) : undefined;
};

/** The local copy by a cloud storage path, also in the folder of the workspace the file was first saved in. */
const locateByStoragePath = async (storagePath: string, workspaceName: string, userId: string | undefined): Promise<string | null> => {
    const info = parseStoragePath(storagePath);
    if (!info) return null;
    const found = await locateLocalFile(workspaceName, info.category, info.filename, userId, storageWorkspaceOf(storagePath));
    return found ? found.url : null;
};

/** The address of the file's copy in the local folder, or null. Never asks the cloud's storage; the registry only while the folder can be read. */
const findLocal = async (ref: string, options: DisplayOptions): Promise<string | null> => {
    const { userId, workspaceName, ownerUserId } = options;
    if (!workspaceName) return null;

    // The workspace's reference map (works offline and signed out)
    const mapped = localPathForRef(ref);
    if (mapped) {
        const info = parseStoragePath(mapped) || { category: mapped.split('/')[0], filename: mapped.split('/').pop() || '' };
        if (info.category && info.filename) {
            const found = await locateLocalFile(workspaceName, info.category, info.filename, userId);
            if (found) return found.url;
        }
    }
    const fileId = extractFileId(ref);
    if (!userId || !fileId) return null;

    // Where this device remembers the file to be stored: that names it in the folder, with no question to the registry
    const remembered = knownStoragePath(userId, fileId);
    if (remembered) return locateByStoragePath(remembered, workspaceName, userId);

    // Not seen on this device before: the registry says where it is stored. It is only worth
    // asking when its answer can lead to a file that can be read right now
    if ((await getLocalFolderAccess(userId).catch(() => null))?.state !== 'granted') return null;
    let meta = await getFileMeta(userId, fileId);
    if (!meta && ownerUserId && ownerUserId !== userId) {
        meta = await getFileMeta(ownerUserId, fileId);
        if (meta) rememberStoragePath(userId, fileId, meta.storagePath);
    }
    return meta?.storagePath ? locateByStoragePath(meta.storagePath, workspaceName, userId) : null;
};

const keepLocal = (ref: string, options: DisplayOptions, url: string): DisplayMedia => {
    const kept: Kept = { url, isLocal: true, madeAt: Date.now(), revision: 0, searched: new Map() };
    resolved.set(keptKey(ref, options.userId), kept);
    referencesByLocalUrl.set(url, ref);
    if (!referencesShared) {
        // The workspace file in the folder is then written with references in place of local addresses
        setLocalUrlReferences(address => referencesByLocalUrl.get(address));
        referencesShared = true;
    }
    return answer(kept);
};

const resolve = async (ref: string, options: DisplayOptions): Promise<DisplayMedia> => {
    const { userId, workspaceName, ownerUserId } = options;
    const key = keptKey(ref, userId);
    // The folder's state as the search begins: a change meanwhile makes the answer be checked again
    const revision = localFolderRevision();
    const fileId = extractFileId(ref) || '';

    const local = await findLocal(ref, options);
    if (local) return keepLocal(ref, options, local);
    // The storage path the folder was just searched by, if any was known
    const searchedBy = knownStoragePath(userId, fileId);

    if (!userId) {
        return {
            url: null, isLocal: false, reason: 'signed-out',
            error: workspaceName ? 'File not found locally. Please sign in for cloud access.' : 'User ID required for file reference resolution',
        };
    }

    // The cloud. An address got a while ago is used again: only the look in the folder was due
    const now = Date.now();
    const before = resolved.get(key);
    if (before && !before.isLocal && now - before.madeAt < CLOUD_URL_TTL_MS) {
        if (before.revision !== revision) {
            before.revision = revision;
            before.searched.clear();
        }
        before.searched.set(workspaceName || '', now);
        return answer(before);
    }
    let url = await resolveFileUrl(userId, ref);
    if (!url && ownerUserId && ownerUserId !== userId) url = await resolveFileUrl(ownerUserId, ref);

    // Asking for the address read the registry: if it names another place than the folder was searched by, look there too
    const stored = knownStoragePath(userId, fileId) || (ownerUserId ? knownStoragePath(ownerUserId, fileId) : undefined);
    if (workspaceName && stored && stored !== searchedBy) {
        rememberStoragePath(userId, fileId, stored);
        const found = await locateByStoragePath(stored, workspaceName, userId);
        if (found) return keepLocal(ref, options, found);
    }

    if (!url) return { url: null, isLocal: false, error: 'File not found', reason: 'missing' };
    const kept: Kept = { url, isLocal: false, madeAt: Date.now(), revision, searched: new Map([[workspaceName || '', Date.now()]]) };
    resolved.set(key, kept);
    return answer(kept);
};

/**
 * The address to show a media reference from (see the order at the top of this file). Never
 * rejects: a failure comes back as { url: null, error }. Answers are kept for the session;
 * failures are not, so they are tried again the next time.
 */
export const resolveDisplayUrl = (ref: string | null | undefined, options: DisplayOptions = {}): Promise<DisplayMedia> => {
    const known = peekDisplayUrl(ref, options);
    if (known) return Promise.resolve(known);
    const fileRef = ref as string;
    const flightKey = [options.userId || '', fileRef, options.workspaceName || '', options.ownerUserId || '', localFolderRevision()].join('|');
    const running = inFlight.get(flightKey);
    if (running) return running;
    const pending: Promise<DisplayMedia> = resolve(fileRef, options)
        .catch((e: unknown): DisplayMedia => ({
            url: null, isLocal: false, reason: 'failed',
            error: (e as { message?: string })?.message || 'Failed to resolve file URL',
        }))
        .finally(() => { if (inFlight.get(flightKey) === pending) inFlight.delete(flightKey); });
    inFlight.set(flightKey, pending);
    return pending;
};

/**
 * Only the copy in the local folder, for a view that shows a cloud address and has just
 * learnt that the folder can be read: nothing is asked of the cloud's storage and no address
 * is refreshed. Null when there is no local copy.
 */
export const findLocalDisplayUrl = async (ref: string, options: DisplayOptions = {}): Promise<DisplayMedia | null> => {
    if (!isFileReference(ref)) return null;
    inThisSession();
    try {
        const local = await findLocal(ref, options);
        return local ? keepLocal(ref, options, local) : null;
    } catch {
        return null;
    }
};

/**
 * Forget what was resolved for a reference, so it is looked up afresh: after its address
 * failed to load (the file was moved or removed, the cloud link went stale), or on refresh.
 */
export const forgetDisplayUrl = (ref: string | null | undefined, userId?: string): void => {
    if (!ref) return;
    inThisSession();
    const key = keptKey(ref, userId);
    const kept = resolved.get(key);
    if (!kept) return;
    resolved.delete(key);
    if (kept.isLocal) forgetLocalUrl(kept.url);
};

/** Views that show a local address, told when one failed to load. */
const failureListeners = new Set<(url: string) => void>();

/** Be told when a local address failed to load in an element; the returned function stops it. */
export const onLocalUrlFailed = (listener: (url: string) => void): (() => void) => {
    failureListeners.add(listener);
    return () => { failureListeners.delete(listener); };
};

/**
 * An element could not load an address. When it is a local file's (found earlier, since moved
 * or removed outside the app), what was found is forgotten and the views that show it are
 * told, so they look the file up again: in the folder first, then in the cloud. Any other
 * address is none of this module's business.
 */
export const reportFailedUrl = (url: string | null | undefined): void => {
    if (!url || !isCurrentLocalUrl(url)) return;
    forgetLocalUrl(url);
    for (const listener of Array.from(failureListeners)) listener(url);
};

/**
 * The file reference a local address was resolved from in this session, or undefined. For
 * code that saves node data: a view may have put the address it shows where the reference
 * belongs, and a local address must never be saved (it dies with the session, and in the
 * desktop app it holds that run's id). Saving the reference instead loses nothing.
 */
export const referenceOfDisplayUrl = (url: string | null | undefined): string | undefined => {
    if (!url) return undefined;
    inThisSession();
    return referencesByLocalUrl.get(url);
};
